<div class="offcanvas-header border-bottom">
  <div>
    <h5 class="offcanvas-title premium-heading mb-1">
      @if (mode === 'ADD') {
        Add Balance Account
      }
      @if (mode === 'VIEW') {
        Balance Account Detail
      }
      @if (mode === 'EDIT') {
        Edit Balance Account
      }
    </h5>
    @if (mode !== 'ADD' && accountData) {
      <div class="premium-subtitle text-uppercase">
        {{ accountData.accountName }}
      </div>
    }
  </div>
  <button type="button" class="btn-close text-reset shadow-none" aria-label="Close"
    [disabled]="saveLoader" (click)="activeOffcanvas.dismiss('Cross click')"></button>
</div>

<div class="offcanvas-body p-4 bg-light custom-scrollbar">
  <!-- Error Alert -->
  @if (formErrorMessage) {
    <div class="alert alert-danger py-2 mb-3 small d-flex justify-content-between align-items-center rounded-3">
      <span>{{ formErrorMessage }}</span>
      <button type="button" class="btn-close small py-1 shadow-none" style="font-size: 0.75rem;"
        (click)="formErrorMessage = ''"></button>
    </div>
  }

  <!-- VIEW MODE (Stacked: Top Label, Value Bottom) -->
  @if (mode === 'VIEW') {
    <div class="d-flex flex-column gap-3 mt-2">
      <!-- Account Name -->
      <div class="d-flex flex-column gap-1">
        <span class="premium-view-label">Account name</span>
        <span class="premium-view-value-text text-uppercase">{{ accountData?.accountName || '-' }}</span>
      </div>
      
      <!-- Last 4 digits -->
      <div class="d-flex flex-column gap-1">
        <span class="premium-view-label">Last 4 digits</span>
        <span class="premium-view-value-text">{{ formatReference(accountData?.accountNumber) }}</span>
      </div>

      <!-- Balance -->
      <div class="d-flex flex-column gap-1">
        <span class="premium-view-label">Balance</span>
        <span class="premium-view-value-text">₹{{ (accountData?.amount || 0) | number : '1.0-0' : 'en-IN' }}</span>
      </div>

      <!-- Status -->
      <div class="d-flex flex-column gap-1">
        <span class="premium-view-label">Status</span>
        <span class="status-label fw-bold" [ngClass]="accountData?.status === 'Active' ? 'text-green' : 'text-red'">
          {{ accountData?.status || 'Active' }}
        </span>
      </div>

      <!-- Linked Payment Accounts Table -->
      <div class="d-flex flex-column gap-2 mt-2">
        <span class="premium-view-label">Linked Payment Accounts</span>
        <div class="card rounded-4 thick-border-gray shadow-none overflow-hidden">
          <div class="table-responsive" infiniteScroll [scrollWindow]="false" [infiniteScrollDistance]="2" [infiniteScrollThrottle]="500" (scrolled)="onScrollTable()">
            <table class="table custom-table align-middle m-0">
              <thead>
                <tr>
                  <th style="width: 40%;" class="fw-semibold">Payment Account</th>
                  <th style="width: 25%;" class="fw-semibold">Mode</th>
                  <th style="width: 35%;" class="fw-semibold">Settlement Type</th>
                </tr>
              </thead>
              <tbody>
                @for (item of paymentAccounts; track item._id; let idx = $index) {
                  <tr>
                    <!-- payment account name as badge with different colors -->
                    <td>
                      <span class="account-ref-badge" [ngStyle]="{ 'background-color': getBadgeColor(item._id, idx) + '22', 'color': getBadgeColor(item._id, idx) }">
                        {{ item.accountName }}
                      </span>
                    </td>
                    <!-- mode: 400w 12px #64748B -->
                    <td class="payment-mode-cell">{{ item.paymentType || '-' }}</td>
                    <!-- settlement type badge with delay days below if delayed -->
                    <td>
                      @if (item.settlementType === 'Immediate') {
                        <span class="settlement-immediate-badge">Immediate</span>
                      } @else if (item.settlementType === 'Auto' || item.settlementType === 'Delayed') {
                        <div class="d-flex flex-column align-items-start gap-1">
                          <span class="settlement-delayed-badge">Delayed</span>
                          <span class="days-cell fw-bold">{{ item.settlementPeriod || 0 }} days</span>
                        </div>
                      } @else {
                        <span>{{ item.settlementType || '-' }}</span>
                      }
                    </td>
                  </tr>
                }

                @if (tableLoader) {
                  <tr>
                    <td colspan="3" class="text-center py-4">
                      <output class="spinner-border spinner-border-sm text-primary" aria-hidden="true"></output>
                    </td>
                  </tr>
                }

                @if (!tableLoader && tableErrorMessage) {
                  <tr>
                    <td colspan="3" class="text-center py-4 text-danger small fw-semibold">
                      {{ tableErrorMessage }}
                    </td>
                  </tr>
                }

                @if (!tableLoader && !tableErrorMessage && paymentAccounts.length === 0) {
                  <tr>
                    <td colspan="3" class="text-center py-4 text-muted small">
                      No linked payment accounts found.
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ADD / EDIT MODE (Form Fields) -->
  @if (mode === 'ADD' || mode === 'EDIT') {
    <form [formGroup]="accountForm" class="d-flex flex-column gap-3">
      <!-- Account Name (Disabled in EDIT mode via TS form control disable) -->
      <div>
        <label for="accountName" class="form-label premium-form-label mb-1">Account name *</label>
        <input type="text" id="accountName" class="form-control premium-input text-uppercase-input" formControlName="accountName" placeholder="E.G. HDFC CURRENT ACCOUNT" (input)="onAccountNameInput($event)">
      </div>

      <!-- Last 4 digits (AccountNumber) -->
      <div>
        <label for="accountNumber" class="form-label premium-form-label mb-1">Last 4 digits *</label>
        <input type="text" id="accountNumber" class="form-control premium-input" formControlName="accountNumber" placeholder="e.g. 4321" maxlength="4" (input)="onAccountNumberInput($event)">
        <div class="bottom-label-text mt-1">
          Enter only the last 4 digits of the bank account
        </div>
      </div>

      <div>
        <label for="amount" class="form-label premium-form-label mb-1">
          {{ mode === 'ADD' ? 'Starting balance *' : 'Balance *' }}
        </label>
        <input type="number" id="amount" class="form-control premium-input" formControlName="amount" placeholder="e.g. ₹0"
          (wheel)="onNumberWheel($event)" (keydown)="onNumberKeyDown($event)">
        @if (mode === 'ADD') {
          <div class="bottom-label-text mt-1">
            Set once at creation. We update the balance from settlements.
          </div>
        }
      </div>

      @if (mode === 'EDIT') {
        <div>
          <label for="statusSwitch" class="form-label premium-form-label m-0">Status</label>
          <div class="form-check form-switch d-flex align-items-center mt-1">
            <input id="statusSwitch" class="form-check-input cursor-pointer switch-blue" type="checkbox" formControlName="status">
            <span class="small ms-2 fw-semibold" [ngClass]="accountForm.get('status')?.value ? 'text-green' : 'text-red'">
              {{ accountForm.get('status')?.value ? 'Active' : 'Inactive' }}
            </span>
          </div>
        </div>
      }
    </form>
  }
</div>

<div class="offcanvas-footer border-top p-3 bg-white d-flex flex-column gap-2">
  @if (mode === 'VIEW') {
    <button class="btn premium-btn-dark w-100 py-2" [disabled]="saveLoader"
      (click)="switchToEdit()">Edit balance account</button>
    <button class="btn premium-btn-cancel w-100 py-2" [disabled]="saveLoader"
      (click)="activeOffcanvas.dismiss()">Cancel</button>
  }

  @if (mode === 'ADD' || mode === 'EDIT') {
    <button class="btn premium-btn-dark w-100 py-2 d-flex justify-content-center align-items-center gap-2"
      [disabled]="accountForm.invalid || saveLoader" (click)="saveAccount()">
      @if (saveLoader) {
        <output class="spinner-border spinner-border-sm" aria-hidden="true"></output>
      }
      @if (!saveLoader) {
        <span>{{ mode === 'ADD' ? 'Save balance account' : 'Save Changes' }}</span>
      }
    </button>
    <button class="btn premium-btn-cancel w-100 py-2" [disabled]="saveLoader"
      (click)="activeOffcanvas.dismiss()">Cancel</button>
  }
</div>
